import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./BreakInPaywall.module.scss"

/**
 * BreakInPaywall — an original, self-contained FEATURE-SPECIFIC paywall.
 *
 * Concept ("Caught"): this screen is what a user sees the instant they tap the
 * locked **Break-in Alerts** feature. Instead of explaining the feature, it
 * *demonstrates* it: the phone's front camera has supposedly just snapped the
 * stranger who fumbled your passcode, and the whole screen becomes a CCTV
 * evidence monitor — scanlines, a blinking REC light, a live timecode, a focus
 * reticle locking onto a captured "intruder" photo. Fear → relief: red threat
 * UI resolves into the brand-blue "armed & safe" CTA.
 *
 * The layout funnels to the YEARLY plan: arming the feature for a year is the
 * single prominent surface, the yearly price is the largest number on screen
 * (with a tiny per-month equivalent), and monthly is demoted to one quiet line.
 *
 * Fully self-contained: no shared paywall components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data.
 */

const EVIDENCE = [
  { k: "device", label: "Device", value: "Unknown · iPhone" },
  { k: "attempts", label: "Failed unlocks", value: "2 wrong passcodes" },
  { k: "where", label: "Location", value: "0.3 mi from home" },
]

const HOW_IT_WORKS = [
  {
    Icon: svg.featureIcons.Breakin,
    title: "Intruder selfie",
    body: "A silent front-camera shot the moment someone fails your code.",
  },
  {
    Icon: svg.featureIcons.PatternLock,
    title: "Attempt log",
    body: "Every wrong PIN, pattern and tap — timestamped and saved.",
  },
  {
    Icon: svg.featureIcons.Wifi,
    title: "Place & time",
    body: "Where and when each break-in attempt happened.",
  },
]

const BreakInPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData

  const [showClose, setShowClose] = useState(false)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#0a0c10")
    actions.setFooterInsetColor("#0a0c10")
    const reveal = setTimeout(() => setRevealed(true), 650)
    const close = setTimeout(() => setShowClose(true), 3000)
    return () => {
      clearTimeout(reveal)
      clearTimeout(close)
    }
  }, [actions])

  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 78
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.max(0, Math.round((1 - ratio) * 100))
  }, [yearlyProduct, monthlyProduct])

  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const hasTrial =
    (yearlyProduct?.hasTrial ?? true) && trialDays > 0 && trialDays <= 31

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined = yearlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel = hasTrial ? "Arm Break-in Alerts free" : "Arm Break-in Alerts"

  return (
    <div className={styles.screen}>
      <Surveillance />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 12 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.5, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 12, paddingBottom: bottomInset + 16 }}
      >
        {/* CCTV status HUD */}
        <div className={styles.hud}>
          <div className={styles.hudLeft}>
            <span className={styles.recDot} />
            REC
            <span className={styles.hudSep}>·</span>
            <span className={styles.hudCam}>CAM&nbsp;01 — FRONT</span>
          </div>
          <Timecode />
        </div>

        {/* Evidence photo */}
        <motion.div
          className={styles.evidenceWrap}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className={styles.reticle}>
            <span className={`${styles.bracket} ${styles.bTL}`} />
            <span className={`${styles.bracket} ${styles.bTR}`} />
            <span className={`${styles.bracket} ${styles.bBL}`} />
            <span className={`${styles.bracket} ${styles.bBR}`} />
          </div>

          <div className={styles.photoFrame}>
            <img
              className={styles.photo}
              src="https://picsum.photos/seed/intruder7/640/720?grayscale"
              alt=""
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.opacity = "0"
              }}
            />
            <div className={styles.photoScan} />
            <div className={styles.photoNoise} />
            <div className={styles.redact} />
            <AnimatePresence>
              {!revealed && (
                <motion.div
                  className={styles.flash}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, times: [0, 0.25, 1] }}
                />
              )}
            </AnimatePresence>
            <div className={styles.photoTag}>
              <span className={styles.photoTagDot} />
              CAPTURED&nbsp;·&nbsp;TODAY 02:14&nbsp;AM
            </div>
          </div>
        </motion.div>

        {/* Evidence chips */}
        <motion.div
          className={styles.evidenceRow}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {EVIDENCE.map((e, i) => (
            <motion.div
              key={e.k}
              className={styles.evChip}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 + i * 0.09, duration: 0.45 }}
            >
              <span className={styles.evLabel}>{e.label}</span>
              <span className={styles.evValue}>{e.value}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Headline */}
        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className={styles.title}>
            Someone just tried
            <br />
            to open <span className={styles.titleAccent}>your vault.</span>
          </h1>
          <p className={styles.subtitle}>
            Turn on <strong>Break-in Alerts</strong> and the next time a wrong
            code is entered, your phone silently photographs whoever&rsquo;s
            holding it — and logs exactly where and when.
          </p>
        </motion.div>

        {/* How it works */}
        <motion.div
          className={styles.how}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          {HOW_IT_WORKS.map((h, i) => (
            <motion.div
              key={h.title}
              className={styles.howItem}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.75 + i * 0.1, duration: 0.5 }}
            >
              <span className={styles.howIcon}>
                <h.Icon />
              </span>
              <div className={styles.howText}>
                <span className={styles.howTitle}>{h.title}</span>
                <span className={styles.howBody}>{h.body}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <CaughtCounter />

        {/* Yearly-funnelled pricing */}
        <motion.div
          className={styles.plan}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
        >
          <div className={styles.planCard}>
            <span className={styles.armedBadge}>
              <span className={styles.armedDot} />
              SAVE {savings}% YEARLY
            </span>

            <div className={styles.planHead}>
              <div className={styles.planHeadText}>
                <span className={styles.planName}>
                  Break-in Alerts · Annual
                </span>
                <span className={styles.planSub}>
                  Full vault security · billed once a year
                </span>
              </div>
              <span className={styles.planShield}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                  <path
                    d="M12 2.6 5 5.2v5.4c0 4.4 3 8.5 7 9.8 4-1.3 7-5.4 7-9.8V5.2L12 2.6Z"
                    fill="#fff"
                  />
                  <path
                    d="m8.8 12 2.2 2.2 4-4.4"
                    stroke="#1f6bff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>

            <div className={styles.priceRow}>
              <div className={styles.priceMain}>
                <span className={styles.priceValue}>{yearlyPrice}</span>
                <span className={styles.priceUnit}>/year</span>
              </div>
              <div className={styles.pricePer}>
                <span className={styles.pricePerValue}>{yearlyPerMonth}</span>
                <span className={styles.pricePerUnit}>/mo</span>
              </div>
            </div>

            {hasTrial && (
              <div className={styles.trialLine}>
                Start with <strong>{trialDays} days free</strong>, then{" "}
                {yearlyPrice}/year
              </div>
            )}
          </div>

          <div className={styles.monthlyLine}>
            Not now? You can also{" "}
            <button onClick={handlePurchase} className={styles.monthlyBtn}>
              arm monthly for {monthlyPrice}/mo
            </button>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className={styles.ctaWrap}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.08, duration: 0.6 }}
        >
          <button className={styles.cta} onClick={handlePurchase}>
            <span className={styles.ctaShimmer} />
            <span className={styles.ctaPulse} />
            <span className={styles.ctaLabel}>{ctaLabel}</span>
            <span className={styles.ctaArrow}>→</span>
          </button>
          <p className={styles.ctaNote}>
            {hasTrial
              ? "No charge today · Cancel anytime in one tap"
              : "Cancel anytime · Your photos never leave your control"}
          </p>
        </motion.div>

        <TrustRow />

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/** Full-screen CCTV monitor backdrop: vignette, grid, drifting scanline. */
const Surveillance = () => {
  return (
    <div className={styles.surveil} aria-hidden>
      <div className={styles.grid} />
      <div className={styles.scanline} />
      <div className={styles.vignette} />
      <div className={styles.flicker} />
    </div>
  )
}

/** Live HH:MM:SS timecode that sells the "recording right now" illusion. */
const Timecode = () => {
  const [t, setT] = useState(() => secondsToCode(2 * 3600 + 14 * 60 + 6))
  const ref = useRef(2 * 3600 + 14 * 60 + 6)

  useEffect(() => {
    const id = setInterval(() => {
      ref.current += 1
      setT(secondsToCode(ref.current))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  return <span className={styles.timecode}>{t}</span>
}

function secondsToCode(total: number) {
  const s = total % 60
  const m = Math.floor(total / 60) % 60
  const h = Math.floor(total / 3600) % 24
  const p = (n: number) => String(n).padStart(2, "0")
  return `${p(h)}:${p(m)}:${p(s)}`
}

/** Momentum / social proof: break-ins caught this week, ticking up. */
const CaughtCounter = () => {
  const [count, setCount] = useState(1284417)
  const ref = useRef(1284417)

  useEffect(() => {
    const id = setInterval(() => {
      ref.current += Math.floor(1 + Math.abs(Math.sin(ref.current)) * 4)
      setCount(ref.current)
    }, 850)
    return () => clearInterval(id)
  }, [])

  return (
    <motion.div
      className={styles.counter}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.95, duration: 0.6 }}
    >
      <span className={styles.counterPulse} />
      <span className={styles.counterValue}>{count.toLocaleString()}</span>
      <span className={styles.counterLabel}>break-ins caught this week</span>
    </motion.div>
  )
}

const TrustRow = () => {
  return (
    <motion.div
      className={styles.trust}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.18, duration: 0.6 }}
    >
      <div className={styles.trustItem}>
        <span className={styles.stars5}>★★★★★</span>
        <span className={styles.trustLabel}>4.8 App Store</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>10M+</span>
        <span className={styles.trustLabel}>vaults guarded</span>
      </div>
      <div className={styles.trustDivider} />
      <div className={styles.trustItem}>
        <span className={styles.trustBig}>🔒</span>
        <span className={styles.trustLabel}>zero-knowledge</span>
      </div>
    </motion.div>
  )
}

export default BreakInPaywall
